import React, { useCallback, useRef, useState } from 'react';
import { IconButton, LinearProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import docIcon from './FiletypeIcons/doc.svg';
import jpgIcon from './FiletypeIcons/jpg.svg';
import mpIcon from './FiletypeIcons/mp.svg';
import pngIcon from './FiletypeIcons/png.svg';
import svgIcon from './FiletypeIcons/svg.svg';
import txtIcon from './FiletypeIcons/txt.svg';
import xlsIcon from './FiletypeIcons/xls.svg';
import pdfIcon from './FiletypeIcons/pdf.svg';
import pptIcon from './FiletypeIcons/ppt.svg';
import { observer } from 'mobx-react-lite';
import { basedAxios as Axios } from '../../store/Store';
import _ from 'lodash';
import EventLabel from '../../components/EventLabel/EventLabel';
import { toJS } from 'mobx';

const FileIcon = ({ src }) => {
  return (
    <>
      <img
        src={src}
        alt={src}
        style={{
          width: '50px',
          maxWidth: '50px',
          maxHeight: '50px',
          height: 'auto',
        }}
      ></img>
    </>
  );
};

export const FileUploader = observer(props => {
  const [showConflictPopper, setShowConflictPopper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);
  const { activePersonNo, orders, contentLayout, updateOrder, allowedFiletypes, environment } = props;

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const onFileSelected = async e => {
    const file = e.target.files[0];
    const order = toJS(orders[activePersonNo]);
    const key = order.sqlStateNo;
    const hasNameConflict = order?.uploadedFiles?.find(u => u.name?.replace(`${key}_${activePersonNo}_`, '') == file?.name.replaceAll(' ', '_'));
    if (hasNameConflict) return setShowConflictPopper(true);
    const result = await toBase64(file).catch(e => Error(e));
    if (result instanceof Error) {
      return;
    }
    if (orders) {
      let order = orders[activePersonNo];
      if (!order.sqlStateNo || order.sqlStateNo != contentLayout.sqlStateNo) updateOrder(order);
    }
    setShowConflictPopper(false);
    setFileOnOrder(file.name, file.type, result);
  };

  const setFileOnOrder = useCallback(
    async (sourceFilename, sourceType, source) => {
      if (orders) {
        let order = orders[activePersonNo];
        let newOrder = _.cloneDeep(order);
        let type = newOrder.uploadedFiles;
        let newType = undefined;
        if (!type) {
          newType = [];
        } else {
          newType = _.cloneDeep(type);
        }
        setUploading(true);
        try {
          const { data } = await Axios.post(`./files/fileUploadTemp/${environment.eventId}`, {
            file: source,
            name: `${order.sqlStateNo + '_' + activePersonNo}_${sourceFilename}`,
            type: sourceType,
          });
          newType.unshift(data);
          newOrder.uploadedFiles = newType;
          updateOrder(newOrder);
          if (fileInputRef) fileInputRef.current.value = '';
        } catch (err) {
          if (err.response?.status == 413) {
            alert('The max allowed file size is 100 MB. Please try again with a smaller file.');
          }
        } finally {
          setUploading(false);
        }
      }
    },
    [orders, activePersonNo, updateOrder, environment.eventId]
  );

  const removeUploadedFile = useCallback(
    file => {
      if (orders) {
        let order = orders[activePersonNo];
        let newOrder = _.cloneDeep(order);
        let type = newOrder.uploadedFiles;
        let newType = type.filter(f => f.url !== file.url);
        newOrder.uploadedFiles = newType;

        updateOrder(newOrder);

        Axios.post(`./files/removeUploadedFile/${order.eventId}`, file);
      }
      setDeleteTarget(null);
    },
    [orders, activePersonNo, updateOrder]
  );

  const confirmDelete = confirmed => {
    if (confirmed == true && deleteTarget) {
      removeUploadedFile(deleteTarget);
      setShowDeleteDialog(false);
    } else {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  let order = null;
  if (orders) {
    order = orders[activePersonNo];
  }

  const key = order ? order.badgeNo || order.sqlStateNo : '';

  return (
    <>
      <label htmlFor="file-upload">
        <div>
          <i className="ri-folder-line" />
        </div>
        <span className="file-uploader text ellipsis">
          {uploading ? <EventLabel name="FileUploadProcessing" groupName="Cms.FileUpload" /> : <EventLabel name="NoFileChosen" groupName="Cms.FileUpload" />}
        </span>
      </label>
      <input ref={fileInputRef} type="file" id="file-upload" accept={allowedFiletypes?.join('')} onChange={e => onFileSelected(e)} />
      {showConflictPopper ? (
        <>
          <br />
          <Alert style={{ textAlign: 'left' }} severity="error">
            <AlertTitle>
              <EventLabel name="FileNameConflict" groupName="Cms.FileUpload" />
            </AlertTitle>
            <EventLabel name="FileWithSameNameAlreadyUploaded" groupName="Cms.FileUpload" />
          </Alert>
        </>
      ) : null}

      {uploading ? <LinearProgress /> : <br />}
      {showDeleteDialog && deleteTarget ? <ConfirmDialog action={confirmDelete} /> : null}
      <ul className="jir-scroll">
        {order?.uploadedFiles?.map((f, i) => {
          return (
            <li key={f.url}>
              <div className="left-col">
                <div className="avatar">
                  <div>
                    <FileType type={f.name.substr(f.name.lastIndexOf('.') + 1, f.name.length)} source={f.url} />
                  </div>
                </div>
                <div className="list-text">
                  {f.name.includes(`${order.sqlStateNo}_${activePersonNo}_`)
                    ? f.name.replace(`${order.sqlStateNo}_${activePersonNo}_`, '')
                    : f.name.replace(`${key}_`, '')}
                </div>
              </div>
              <div className="delete-btn">
                <IconButton
                  onClick={() => {
                    setShowDeleteDialog(true);
                    setDeleteTarget(f);
                  }}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
});

const FileType = ({ type, source }) => {
  switch (type) {
    case 'pdf':
      return <FileIcon src={pdfIcon} />;
    case 'doc':
    case 'docx':
      return <FileIcon src={docIcon} />;
    case 'jpg':
      return <FileIcon src={source || jpgIcon} />;
    case 'gif':
    case 'jif':
      return <FileIcon src={source} />;
    case 'mp4':
      return <FileIcon src={mpIcon} />;
    case 'png':
      return <FileIcon src={source || pngIcon} />;
    case 'svg':
      return <FileIcon src={svgIcon} />;
    case 'txt':
      return <FileIcon src={txtIcon} />;
    case 'xls':
    case 'xlsx':
      return <FileIcon src={xlsIcon} />;
    case 'ppt':
    case 'pptx':
      return <FileIcon src={pptIcon} />;
    default:
      return null;
  }
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function ConfirmDialog({ action }) {
  return (
    <div>
      <Dialog
        open={true}
        TransitionComponent={Transition}
        keepMounted
        onClose={action}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle id="alert-dialog-slide-title">
          <EventLabel name="DeleteFile" groupName="Cms.FileUpload" />
        </DialogTitle>
        <DialogContent style={{ width: 350 }}>
          <DialogContentText id="alert-dialog-slide-description">
            <EventLabel name="FileDeletePerm" groupName="Cms.FileUpload" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={action} color="default">
            <EventLabel name="Cancel" groupName="Cms.FileUpload" />
          </Button>
          <Button
            onClick={() => action(true)}
            variant="contained"
            style={{
              background: 'rgb(223, 65, 54)',
              color: '#fff',
              boxShadow: 'none',
            }}
          >
            <EventLabel name="DeleteConfirm" groupName="Cms.FileUpload" />
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
